import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MachineContext } from '../DataContexts/MachineContext';
import {
  ACCOUNT_STATE_ORDER,
  OVEREENKOMST_ORDER,
  getOvereenkomstLabel,
  formatEuro,
  AccountStateBadge,
} from '../MachineManager/accountsShared';
import {
  TODAY_STATUS_LABELS,
  TODAY_STATUS_ORDER,
  normalizeTodayStatus,
} from '../MachineManager/openDays';
import { DashboardWidget, WidgetValue, WidgetScopeControl } from './DashboardWidgets';
import './AccountStatWidgets.css';

// DJB-128 moved these stats from the Accounts Overview footer to the Dashboard;
// DJB-140/143/144/145 rebuilt them on the DJB-139 widget templates, and
// DJB-148/149/150/151 reworked the presentation (revenue groups, Fleet layout,
// under-target table, state table). Everything still derives from the full
// machine list in MachineContext — same figures.

// Rounded share of a total; individual shares sum to ~100%
export const formatPercent = (part, total) =>
  total > 0 ? `${Math.round((part / total) * 100)}%` : '0%';

const isOnline = (machine) => {
  if (!machine.lastSeen || machine.lastSeen.length < 6) return false;
  const lastSeenDate = new Date(
    machine.lastSeen[0], machine.lastSeen[1] - 1, machine.lastSeen[2],
    machine.lastSeen[3], machine.lastSeen[4], machine.lastSeen[5]
  );
  const diffMinutes = (new Date() - lastSeenDate) / (1000 * 60);
  return diffMinutes <= 60;
};

const machineIncome = (machine) =>
  (machine.currentCoinDeposit || 0) + (machine.currentIncomeFromPayconiq || 0);

const isInFleet = (machine) => (machine.fleetState || '').toUpperCase() === 'IN_FLEET';

const daysSinceEmptied = (machine) => {
  if (!machine.coinsLastEmptied || machine.coinsLastEmptied.length < 3) return null;
  const lastClearDate = new Date(
    machine.coinsLastEmptied[0], machine.coinsLastEmptied[1] - 1, machine.coinsLastEmptied[2]
  );
  return Math.floor(Math.abs(new Date() - lastClearDate) / (1000 * 60 * 60 * 24));
};

// Total / online / in-fleet / income / monthly average grouped per province,
// provinces sorted by total. Used with the full list (Current Revenue) and
// fleet-filtered lists (Fleet widget).
const computeProvinceStats = (machines) => {
  const provinceGroups = machines.reduce((acc, machine) => {
    const province = machine.province || 'Unknown';
    (acc[province] = acc[province] || []).push(machine);
    return acc;
  }, {});
  return Object.entries(provinceGroups)
    .map(([province, group]) => [province, {
      total: group.length,
      online: group.filter(isOnline).length,
      inFleet: group.filter(isInFleet).length,
      income: group.reduce((sum, machine) => sum + machineIncome(machine), 0),
      // DJB-192 r2: period-normalized avg/machine/month, same as widget total
      avgMonthly: averageMonthlyRate(group),
    }])
    .sort(([, a], [, b]) => b.total - a.total);
};

// DJB-148: Dev & Tbd are placeholder provinces (dev rigs, machines without a
// location yet) — they stay out of the revenue-per-province breakdown.
const REVENUE_EXCLUDED_PROVINCES = ['dev', 'tbd'];

// DJB-144: a Basic machine underperforms when it averages under €1,50/day AND
// hasn't been emptied (boekhoudperiode reset) in at least 14 days. Machines
// that were never emptied have no measurable period, so they don't count.
const BAD_PERFORMER_MIN_CENTS_PER_DAY = 150;
const BAD_PERFORMER_MIN_DAYS = 14;

const isBadPerformer = (machine) => {
  if ((machine.overeenkomst || '').toUpperCase() !== 'BASIC') return false;
  const days = daysSinceEmptied(machine);
  if (days === null || days < BAD_PERFORMER_MIN_DAYS) return false;
  return machineIncome(machine) / days < BAD_PERFORMER_MIN_CENTS_PER_DAY;
};

// DJB-145: pending & archived machines are out of fleet, every other state is
// in fleet; dormant & dead are the ones that need a checkup.
// DJB-192: month length used to project per-day rates to a monthly figure
const DAYS_PER_MONTH = 30;

// DJB-192: a machine's income projected to a ~30-day month (avg/day × 30);
// null when it was never emptied, i.e. has no measurable period
const machineMonthlyRate = (machine) => {
  const days = daysSinceEmptied(machine);
  return days ? (machineIncome(machine) / days) * DAYS_PER_MONTH : null;
};

// Mean monthly rate over the in-fleet machines with a measurable period.
// DJB-192 r2: this widget-total derivation is reused per plan and per
// province, so every avg column normalizes for period length the same way.
const averageMonthlyRate = (machines) => {
  const rates = machines
    .filter(isInFleet)
    .map(machineMonthlyRate)
    .filter((rate) => rate !== null);
  return rates.length > 0
    ? rates.reduce((sum, rate) => sum + rate, 0) / rates.length
    : null;
};

const IN_FLEET_STATES = ['active', 'dormant', 'dead', 'suspended'];
const OUT_FLEET_STATES = ['pending', 'archived'];
const CHECKUP_STATES = ['dead', 'dormant'];

export const useAccountStatistics = () => {
  const { machines = [] } = useContext(MachineContext);

  return useMemo(() => {
    const totalMachines = machines.length;
    const onlineMachines = machines.filter(isOnline).length;
    const inFleetMachines = machines.filter(isInFleet).length;

    // Calculate total income, split into cash and mobile pay
    const totalCash = machines.reduce((sum, machine) => sum + (machine.currentCoinDeposit || 0), 0);
    const totalMobile = machines.reduce((sum, machine) => sum + (machine.currentIncomeFromPayconiq || 0), 0);
    const totalIncome = totalCash + totalMobile;

    const provinceStats = computeProvinceStats(machines);

    // DJB-192: a plain income/machines average is misleading because each
    // machine's income spans a different period (since its last emptying).
    // Instead take every in-fleet machine with a measurable period, project
    // its per-day rate to a ~30-day month, and average those rates.
    const avgIncomePerMachinePerMonth = averageMonthlyRate(machines);

    const accountStateCounts = ACCOUNT_STATE_ORDER.reduce((acc, state) => {
      acc[state] = machines.filter(m => (m.accountState || '').toLowerCase() === state).length;
      return acc;
    }, {});

    // Group machines by overeenkomst, falling back to UNASSIGNED when missing;
    // DJB-148 tracks the revenue next to the machine count, DJB-192 r2 the
    // period-normalized monthly average alongside
    const overeenkomstGroups = machines.reduce((acc, machine) => {
      const key = machine.overeenkomst || 'UNASSIGNED';
      (acc[key] = acc[key] || []).push(machine);
      return acc;
    }, {});
    const statsForPlan = (group = []) => ({
      count: group.length,
      income: group.reduce((sum, machine) => sum + machineIncome(machine), 0),
      avgMonthly: averageMonthlyRate(group),
    });
    const overeenkomstStats = [
      ...OVEREENKOMST_ORDER.map(key => [key, statsForPlan(overeenkomstGroups[key])]),
      ...Object.entries(overeenkomstGroups)
        .filter(([key]) => !OVEREENKOMST_ORDER.includes(key))
        .map(([key, group]) => [key, statsForPlan(group)])
        .sort(([, a], [, b]) => b.count - a.count),
    ];

    // DJB-150: worst earners first — that's the order to act on them in
    const badPerformers = machines
      .filter(isBadPerformer)
      .map(machine => ({
        ...machine,
        avgIncomePerDay: machineIncome(machine) / daysSinceEmptied(machine),
      }))
      .sort((a, b) => a.avgIncomePerDay - b.avgIncomePerDay);

    // Dead machines first — they need attention the most
    const checkupMachines = machines
      .filter(m => CHECKUP_STATES.includes((m.accountState || '').toLowerCase()))
      .sort((a, b) => {
        const stateDiff =
          CHECKUP_STATES.indexOf((a.accountState || '').toLowerCase()) -
          CHECKUP_STATES.indexOf((b.accountState || '').toLowerCase());
        return stateDiff !== 0 ? stateDiff : (a.name || '').localeCompare(b.name || '');
      });

    return {
      totalMachines,
      onlineMachines,
      inFleetMachines,
      totalIncome,
      totalCash,
      totalMobile,
      avgIncomePerMachinePerMonth,
      provinceStats,
      overeenkomstStats,
      accountStateCounts,
      badPerformers,
      checkupMachines,
    };
  }, [machines]);
};

// Monthly average with a safe fallback when the group holds no in-fleet
// machine with a measurable period
const formatMonthlyAverage = (avgMonthly) =>
  avgMonthly === null ? '—' : formatEuro(Math.round(avgMonthly));

// DJB-192 r2: some machines carry their province ALL CAPS — display them in
// title case (hyphenated names like Oost-Vlaanderen keep both capitals)
const titleCaseProvince = (name) =>
  name.toLowerCase().replace(/(^|[\s-])\S/g, (match) => match.toUpperCase());

// DJB-148: aligned name / total / secondary-figures grid used by the
// Per Plan and Per Province revenue groups
const RevenueBreakdownGroup = ({ title, columns, rows }) => (
  <div className="revenue-group">
    <div className="widget-sublabel">{title}</div>
    <div
      className="revenue-table"
      style={{ gridTemplateColumns: `minmax(0, 1fr) repeat(${columns.length}, max-content)` }}
    >
      <span className="revenue-col-head" aria-hidden="true" />
      {columns.map((column) => (
        <span key={column} className="revenue-col-head">{column}</span>
      ))}
      {rows.map(({ key, label, cells }) => (
        <React.Fragment key={key}>
          <span className="revenue-cell revenue-cell-name">{label}</span>
          {cells.map((cell, index) => (
            <span
              key={columns[index]}
              className={`revenue-cell ${index === 0 ? 'revenue-cell-total' : 'revenue-cell-sub'}`}
            >
              {cell}
            </span>
          ))}
        </React.Fragment>
      ))}
    </div>
  </div>
);

// DJB-140, reworked by DJB-148 into groups; DJB-213 folds the first two
// (the in-fleet total + DJB-192's monthly per-machine average, and the
// payment-method split) into one "Revenue breakdown" group: a hero tile
// for the total with the average underneath, and half-height Cash /
// Mobile pay tiles below it. Then revenue per plan (minus Unassigned) and
// revenue per province (minus Dev/Tbd).
export const CurrentRevenueWidget = () => {
  const statistics = useAccountStatistics();
  const {
    totalIncome, totalCash, totalMobile, avgIncomePerMachinePerMonth,
    provinceStats, overeenkomstStats,
  } = statistics;

  const planRows = overeenkomstStats
    .filter(([overeenkomst]) => overeenkomst !== 'UNASSIGNED')
    .map(([overeenkomst, stats]) => ({
      key: overeenkomst,
      label: getOvereenkomstLabel(overeenkomst),
      cells: [
        formatEuro(stats.income),
        formatPercent(stats.income, totalIncome),
        formatMonthlyAverage(stats.avgMonthly),
      ],
    }));

  const provinceRows = provinceStats
    .filter(([province]) => !REVENUE_EXCLUDED_PROVINCES.includes(province.toLowerCase()))
    .map(([province, stats]) => ({
      key: province,
      label: titleCaseProvince(province),
      cells: [
        formatEuro(stats.income),
        formatPercent(stats.income, totalIncome),
        formatMonthlyAverage(stats.avgMonthly),
      ],
    }));

  const methodTiles = [
    { key: 'cash', label: 'Cash', amount: totalCash },
    { key: 'mobile', label: 'Mobile pay', amount: totalMobile },
  ];

  return (
    <DashboardWidget title="Current Revenue" variant="long" className="current-revenue-widget">
      {/* DJB-213 (supersedes DJB-168's two side-by-side containers): one big
          centered tile carries the dominant total with the per-machine
          average beneath it; the payment-method tiles sit under it at half
          its height */}
      <div className="revenue-group revenue-breakdown">
        <div className="widget-sublabel">Revenue breakdown</div>
        <div className="revenue-breakdown-grid">
          <div className="revenue-hero-tile">
            <div className="revenue-hero-total">{formatEuro(totalIncome)}</div>
            <div className="revenue-hero-caption">Total in-fleet revenue</div>
            {/* DJB-192: normalized for period length — avg/day per machine × ~30 */}
            <div className="revenue-hero-avg">
              <span className="revenue-hero-avg-value">
                {formatMonthlyAverage(avgIncomePerMachinePerMonth)}
              </span>
              <span className="revenue-hero-avg-caption">avg / machine / month</span>
            </div>
          </div>
          {methodTiles.map(({ key, label, amount }) => (
            <div key={key} className="widget-tile revenue-method-tile">
              <span className="widget-tile-label">{label}</span>
              <span className="widget-tile-value">{formatEuro(amount)}</span>
              <span className="widget-tile-pct">{formatPercent(amount, totalIncome)} of total</span>
            </div>
          ))}
        </div>
      </div>
      {/* DJB-192: renamed from Per Overeenkomst, % column like Per Province;
          r2 swaps both AVG columns to the period-normalized monthly figure */}
      <RevenueBreakdownGroup
        title="Per Plan"
        columns={['Total', '%', 'AVG / machine / month']}
        rows={planRows}
      />
      <RevenueBreakdownGroup
        title="Per Province"
        columns={['Total', '%', 'AVG / machine / month']}
        rows={provinceRows}
      />
    </DashboardWidget>
  );
};

// DJB-143: fleet scopes for the Machines widget toggle
const FLEET_SCOPES = [
  { key: 'in-fleet', label: 'In fleet' },
  { key: 'out-fleet', label: 'Out fleet' },
  { key: 'all-fleet', label: 'All' },
];

const filterByFleetScope = (machines, scope) => {
  if (scope === 'in-fleet') {
    return machines.filter(m => (m.fleetState || '').toUpperCase() === 'IN_FLEET');
  }
  if (scope === 'out-fleet') {
    return machines.filter(m => (m.fleetState || '').toUpperCase() === 'OUT_FLEET');
  }
  return machines;
};

// DJB-282: Open / Closed / Unknown today, read from the todayStatus the
// workdays fetch merged into MachineContext (DJB-258, Europe/Brussels) and
// normalised exactly like the Today column of the All machines table
// (DJB-253) — nothing is recomputed from openDays here. Every machine lands
// in exactly one bucket, so the three counts add up to the list length.
export const countTodayStatuses = (machines) =>
  machines.reduce(
    (acc, machine) => {
      acc[normalizeTodayStatus(machine.todayStatus)] += 1;
      return acc;
    },
    TODAY_STATUS_ORDER.reduce((acc, status) => ({ ...acc, [status]: 0 }), {})
  );

// DJB-143 machine counts + online state per province, scoped by the top-right
// in-fleet / out-fleet / all-fleet toggle; DJB-149 renamed it to Fleet,
// defaulted the toggle to in-fleet and tidied the per-province columns.
// DJB-282 adds the Open / Closed / Unknown today tiles under the hero count.
export const FleetWidget = () => {
  const { machines = [] } = useContext(MachineContext);
  const [fleetScope, setFleetScope] = useState('in-fleet');

  const stats = useMemo(() => {
    const scoped = filterByFleetScope(machines, fleetScope);
    return {
      total: scoped.length,
      online: scoped.filter(isOnline).length,
      // same scope as the hero count, so the three tiles sum to it
      today: countTodayStatuses(scoped),
      provinceStats: computeProvinceStats(scoped),
    };
  }, [machines, fleetScope]);

  return (
    <DashboardWidget
      title="Fleet"
      variant="long"
      className="fleet-widget"
      actions={
        <WidgetScopeControl
          label="Fleet filter"
          options={FLEET_SCOPES}
          value={fleetScope}
          onChange={setFleetScope}
        />
      }
    >
      <WidgetValue
        value={stats.total}
        caption={
          <span className="online-count">
            {stats.online} online ({formatPercent(stats.online, stats.total)})
          </span>
        }
      />
      <div className="widget-sublabel">Today</div>
      <div className="fleet-today-tiles" role="list" aria-label="Status today">
        {TODAY_STATUS_ORDER.map((status) => (
          <div
            key={status}
            className={`fleet-today-tile ${status}`}
            role="listitem"
            data-status={status}
          >
            <span className="fleet-today-value">{stats.today[status]}</span>
            <span className="fleet-today-label">
              {TODAY_STATUS_LABELS[status]}
              {status !== 'unknown' && ' today'}
            </span>
            <span className="fleet-today-pct">
              {formatPercent(stats.today[status], stats.total)}
            </span>
          </div>
        ))}
      </div>
      <div className="widget-sublabel">Per province</div>
      <div className="fleet-table">
        <span className="revenue-col-head" aria-hidden="true" />
        <span className="revenue-col-head">Machines</span>
        <span className="revenue-col-head">Online</span>
        {stats.provinceStats.map(([province, provinceStats]) => (
          <React.Fragment key={province}>
            <span className="revenue-cell revenue-cell-name">{province}</span>
            <span className="revenue-cell revenue-cell-total">{provinceStats.total}</span>
            <span className="revenue-cell revenue-cell-sub">
              <span className="online-count">{provinceStats.online}</span>{' '}
              ({formatPercent(provinceStats.online, provinceStats.total)})
            </span>
          </React.Fragment>
        ))}
      </div>
    </DashboardWidget>
  );
};

// DJB-144 overeenkomst breakdown; DJB-150 stretched it to the long template
// and turned the upgrade-target alert into an actionable under-target table
// (same pattern as the Account States checkup list)
export const OvereenkomstenWidget = () => {
  const statistics = useAccountStatistics();
  const { badPerformers } = statistics;
  return (
    // DJB-183: widget title renamed to Plans; component/class names stay
    <DashboardWidget title="Plans" variant="long" className="overeenkomsten-widget">
      <div className="widget-columns">
        <div className="widget-col">
          <div className="widget-sublabel">Per type</div>
          <div className="widget-rows">
            {statistics.overeenkomstStats.map(([overeenkomst, stats]) => (
              <div key={overeenkomst} className="widget-row">
                <span className="widget-row-name">{getOvereenkomstLabel(overeenkomst)}</span>
                <span className="widget-row-value">{stats.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="widget-col">
          <div className="widget-sublabel">
            Onder de minimumgrens · {badPerformers.length}
          </div>
          {badPerformers.length === 0 ? (
            <div className="widget-empty-note">
              Alle Basic-klanten zitten boven de minimumgrens.
            </div>
          ) : (
            <div className="widget-rows checkup-list">
              {badPerformers.map(machine => (
                <Link
                  key={machine.id}
                  className="widget-row checkup-row"
                  to={`/machines?tab=list&id=${machine.id}`}
                >
                  <span className="widget-row-name">{machine.name || machine.id}</span>
                  <span className="underperformer-rate">
                    {formatEuro(machine.avgIncomePerDay)}
                    <span className="underperformer-rate-unit"> / dag</span>
                  </span>
                </Link>
              ))}
            </div>
          )}
          <div className="widget-empty-note underperformer-note" role="status">
            {badPerformers.length}{' '}
            {badPerformers.length === 1 ? 'klant ligt' : 'klanten liggen'} onder de
            minimumgrens — de minimumgrens geldt voor Basic-klanten met een gemiddeld
            inkomen lager dan €1,50 per dag over een periode langer dan 14 dagen.
          </div>
        </div>
      </div>
    </DashboardWidget>
  );
};

// DJB-145: states grouped in-fleet vs out-of-fleet, plus a checkup list of
// the dormant & dead machines. DJB-151 swapped the state pills for a table
// with color-coded state text — the checkup list keeps its pills.
export const AccountStatesWidget = () => {
  const statistics = useAccountStatistics();
  const { accountStateCounts, checkupMachines, totalMachines } = statistics;
  const countFor = (states) => states.reduce((sum, state) => sum + accountStateCounts[state], 0);

  const renderStateGroup = (label, states) => (
    <div className="state-group">
      <div className="widget-sublabel">
        {label} · {countFor(states)}
      </div>
      <div className="widget-rows state-table">
        {states.map(state => (
          <div key={state} className="widget-row state-table-row">
            <span className={`state-table-state ${state}`}>{state}</span>
            <span className="widget-row-value">{accountStateCounts[state]}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <DashboardWidget title="Account States" variant="long" className="account-states-widget">
      <div className="widget-columns">
        <div className="widget-col">
          {renderStateGroup('In fleet', IN_FLEET_STATES)}
          {renderStateGroup('Out of fleet', OUT_FLEET_STATES)}
          <div className="widget-empty-note">{totalMachines} machines in total</div>
        </div>
        <div className="widget-col">
          <div className="widget-sublabel">
            Needs checkup · {checkupMachines.length}
          </div>
          {checkupMachines.length === 0 ? (
            <div className="widget-empty-note">No dormant or dead machines.</div>
          ) : (
            <div className="widget-rows checkup-list">
              {checkupMachines.map(machine => (
                <Link
                  key={machine.id}
                  className="widget-row checkup-row"
                  to={`/machines?tab=list&id=${machine.id}`}
                >
                  <span className="widget-row-name">{machine.name || machine.id}</span>
                  <AccountStateBadge state={machine.accountState} />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardWidget>
  );
};
